import { createRoot } from 'react-dom/client'

import App from './App.tsx'
import { ErrorBoundary } from './components/ErrorBoundary.tsx'
import { StorefrontSiteConfigGate, StorefrontSiteConfigProvider } from './contexts/site-config.tsx'
import { ErrorFallback } from './ErrorFallback.tsx'
import { API_BASE_URL } from './lib/apiBaseUrl.ts'
import { installFrontendTracePropagation } from './lib/browserTraceContext.ts'
import './i18n'

import "./main.css"

const STALE_CHUNK_RELOAD_STORAGE_KEY = 'brasiltec:stale-chunk-reload-at'
const STALE_CHUNK_RELOAD_WINDOW_MS = 30_000
const DYNAMIC_IMPORT_ERROR_PATTERNS = [
  'Failed to fetch dynamically imported module',
  'Importing a module script failed',
  'error loading dynamically imported module',
  'Unable to preload CSS',
]

const isStaleChunkError = (error: unknown) => {
  const message = error instanceof Error ? error.message : String(error ?? '')
  return DYNAMIC_IMPORT_ERROR_PATTERNS.some((pattern) => message.includes(pattern))
}

const reloadOnceForStaleChunkError = (error: unknown) => {
  if (typeof window === 'undefined' || !isStaleChunkError(error)) return false

  const now = Date.now()
  let lastReloadAt = 0

  try {
    lastReloadAt = Number(window.sessionStorage.getItem(STALE_CHUNK_RELOAD_STORAGE_KEY) ?? '0')
  } catch {
    lastReloadAt = 0
  }

  if (Number.isFinite(lastReloadAt) && now - lastReloadAt < STALE_CHUNK_RELOAD_WINDOW_MS) {
    return false
  }

  try {
    window.sessionStorage.setItem(STALE_CHUNK_RELOAD_STORAGE_KEY, String(now))
  } catch {
    // Continue with the reload even when sessionStorage is unavailable.
  }

  window.location.reload()
  return true
}

if (typeof window !== 'undefined') {
  installFrontendTracePropagation({ apiBase: API_BASE_URL })

  window.addEventListener('vite:preloadError', (event) => {
    const preloadEvent = event as Event & { payload?: unknown }

    if (reloadOnceForStaleChunkError(preloadEvent.payload)) {
      event.preventDefault()
    }
  })
}

createRoot(document.getElementById('root')!).render(
  <ErrorBoundary
    FallbackComponent={ErrorFallback}
    onError={(error) => {
      reloadOnceForStaleChunkError(error)
    }}
  >
    <StorefrontSiteConfigProvider>
      <StorefrontSiteConfigGate>
        <App />
      </StorefrontSiteConfigGate>
    </StorefrontSiteConfigProvider>
   </ErrorBoundary>
)
